import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin, ArrowRight } from "lucide-react";
import { LOGO } from "../lib/site";
import { useSite } from "../context/SiteContext";

const Col = ({ title, links }) => (
  <div>
    <p className="mako-label text-[#7A7F85]">{title}</p>
    <ul className="mt-6 space-y-3">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="mako-focus text-sm text-white/70 transition-colors duration-300 hover:text-white"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => {
  const { settings } = useSite();
  const contact = settings?.contact || {};
  const social = settings?.social || {};

  return (
    <footer data-testid="site-footer" className="bg-[#111111] text-white">
      <div className="mako-grid-bg-dark">
        <div className="mx-auto max-w-[1400px] px-6 pb-16 pt-20 lg:px-10 lg:pt-28">
          <div className="flex flex-col gap-10 border-b border-white/10 pb-14 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <img
                src={LOGO.primary}
                alt="MAKO Mats"
                className="h-12 w-auto brightness-0 invert lg:h-14"
              />
              <p className="mako-tagline mt-8 text-xs text-white/60 sm:text-sm">
                Built for impact.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="group inline-flex items-center gap-4 bg-[#0F4C81] px-8 py-5 text-[0.6875rem] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#1463A5]"
            >
              Request a quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid gap-12 py-14 sm:grid-cols-2 lg:grid-cols-4">
            <Col
              title="Products"
              links={[
                { to: "/mats/puzzle-mats", label: "MAKO Puzzle" },
                { to: "/mats/tatami-tiles", label: "MAKO Tatami" },
                { to: "/mats/rollout-mats", label: "MAKO Rollout" },
                { to: "/wall-pads", label: "MAKO Wall" },
                { to: "/custom-solutions", label: "MAKO Custom" },
              ]}
            />
            <Col
              title="Company"
              links={[
                { to: "/about", label: "About MAKO" },
                { to: "/installations", label: "Installations" },
                { to: "/custom-solutions", label: "Custom Solutions" },
                { to: "/contact", label: "Contact" },
                { to: "/admin/login", label: "Admin" },
              ]}
            />
            <Col
              title="Resources"
              links={[
                { to: "/resources", label: "Knowledge hub" },
                {
                  to: "/resources/puzzle-mats-vs-tatami-tiles-vs-rollout-mats",
                  label: "Compare mat systems",
                },
                { to: "/resources/how-to-choose-mats-for-an-mma-gym", label: "Mats for MMA" },
                { to: "/resources/what-surface-is-best-for-bjj", label: "Surfaces for BJJ" },
                { to: "/resources/why-wall-padding-matters", label: "Why wall padding matters" },
              ]}
            />
            <div>
              <p className="mako-label text-[#7A7F85]">Contact</p>
              <ul className="mt-6 space-y-3 text-sm text-white/70">
                <li data-testid="footer-phone">{contact.phone}</li>
                <li data-testid="footer-email">{contact.email}</li>
                <li>{contact.address}</li>
                <li className="text-white/50">{contact.coverage}</li>
              </ul>
              <div className="mt-7 flex gap-3">
                {[
                  { href: social.instagram, Icon: Instagram, label: "Instagram" },
                  { href: social.youtube, Icon: Youtube, label: "YouTube" },
                  { href: social.linkedin, Icon: Linkedin, label: "LinkedIn" },
                ].map(({ href, Icon, label }) => (
                  <a
                    key={label}
                    href={href || "#"}
                    aria-label={label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mako-focus flex h-10 w-10 items-center justify-center border border-white/15 text-white/70 transition-colors duration-300 hover:border-white hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <p className="mako-tagline border-t border-white/10 pt-12 text-[0.625rem] text-white/40 sm:text-xs">
            Professional combat surfaces for modern training spaces.
          </p>

          <div className="mt-10 flex flex-col gap-6 text-xs text-white/40 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                ["privacy-policy", "Privacy Policy"],
                ["terms-and-conditions", "Terms and Conditions"],
                ["shipping-and-delivery", "Shipping and Delivery"],
                ["returns-and-cancellations", "Returns and Cancellations"],
                ["warranty", "Warranty"],
                ["website-terms", "Website Terms"],
              ].map(([slug, label]) => (
                <Link
                  key={slug}
                  to={`/legal/${slug}`}
                  className="mako-focus transition-colors duration-300 hover:text-white"
                >
                  {label}
                </Link>
              ))}
            </div>
            <p>© {new Date().getFullYear()} MAKO Mats. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
